import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import SearchBar from "./components/SearchBar";
import SearchResults from "./components/SearchResults";
import LanguageSwitcher from "./components/LanguageSwitcher";
import Filters from "./components/Filters";
import Login from "./components/Login";
import AuthCallback from "./components/AuthCallback";
import { SynonymGroup, PartOfSpeech, Register } from "./types";
import { api } from "./services/api";
import { useLanguage } from "./contexts/LanguageContext";
import { useAuth } from "./contexts/AuthContext";
import { useTranslations } from "./translations";
import "./App.css";

function MainApp() {
  const [results, setResults] = useState<SynonymGroup[]>([]);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [partOfSpeech, setPartOfSpeech] = useState<PartOfSpeech | "">("");
  const [register, setRegister] = useState<Register | "">("");
  const [minFrequency, setMinFrequency] = useState<number>(1);
  const { language } = useLanguage();
  const { isAuthenticated, logout } = useAuth();
  const t = useTranslations(language);

  const handleSearch = async (searchQuery: string) => {
    setQuery(searchQuery);
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.search({
        query: searchQuery,
        part_of_speech: partOfSpeech || undefined,
        register: register || undefined,
        min_frequency: minFrequency > 1 ? minFrequency : undefined,
      });
      setResults(response.results);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.errorOccurred);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearFilters = () => {
    setPartOfSpeech("");
    setRegister("");
    setMinFrequency(1);
  };

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="app-title">{t.appTitle}</h1>
            <p className="app-subtitle">{t.appSubtitle}</p>
          </div>
          <div className="header-actions">
            <LanguageSwitcher />
            <button className="logout-button" onClick={logout}>
              {t.logout}
            </button>
          </div>
        </div>
      </header>

      <main className="app-main">
        <SearchBar onSearch={handleSearch} />

        <Filters
          partOfSpeech={partOfSpeech}
          register={register}
          minFrequency={minFrequency}
          onPartOfSpeechChange={setPartOfSpeech}
          onRegisterChange={setRegister}
          onMinFrequencyChange={setMinFrequency}
          onClearFilters={handleClearFilters}
        />

        {error && <div className="error-message">{error}</div>}

        <SearchResults results={results} query={query} isLoading={isLoading} />
      </main>

      <footer className="app-footer">
        <p>{t.footerText}</p>
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<MainApp />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/error" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
